import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getAuthHeaders } from '../../auth';
import { usePermissions } from '../../services/PermissionsContext';
import PopoutWindow from '../common/PopoutWindow';
import JobForm from '../documentation/JobForm';
import QuickReferenceForm from './QuickReferenceForm';

const API_BASE = '/api/admin/quick-references/';
const DEPTS_URL = '/api/admin/departments/';
const JOBS_URL = '/api/documentation/jobs/';
const MODULE = 'QUICK REFERENCE';

const fmtDateTime = (val) => {
    if (!val) return '-';
    const d = new Date(val);
    if (isNaN(d)) return val;
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const detailsPreview = (html) => {
    const text = (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length > 100 ? `${text.slice(0, 100)}...` : text;
};

const QuickReference = () => {
    const { hasPermission } = usePermissions();
    const canView = hasPermission(MODULE, 'can_view');
    const canCreate = hasPermission(MODULE, 'can_create');
    const canEdit = hasPermission(MODULE, 'can_edit') || hasPermission(MODULE, 'can_delete');
    const canDelete = hasPermission(MODULE, 'can_delete');

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [departments, setDepartments] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [jobPreview, setJobPreview] = useState({ open: false, job: null });
    const [jobLoading, setJobLoading] = useState(false);
    // One entry per open form window — unique key guarantees a distinct
    // browser window (window.open reuses a window when name matches).
    const [popouts, setPopouts] = useState([]); // { key, mode, row }

    const fetchRows = useCallback(async (q = search) => {
        setLoading(true);
        try {
            const url = q ? `${API_BASE}?search=${encodeURIComponent(q)}` : API_BASE;
            const res = await fetch(url, { headers: getAuthHeaders(false) });
            if (!res.ok) throw new Error('Failed to load quick references');
            const data = await res.json();
            setRows(Array.isArray(data) ? data : (data.results || []));
        } catch (e) {
            toast.error(e.message || 'Failed to load quick references');
        } finally {
            setLoading(false);
        }
    }, [search]);

    const fetchDepartments = useCallback(async () => {
        try {
            const res = await fetch(DEPTS_URL, { headers: getAuthHeaders(false) });
            if (!res.ok) return;
            const data = await res.json();
            const list = Array.isArray(data) ? data : (data.results || []);
            setDepartments(list.map(d => ({ id: d.id, text: d.name })));
        } catch (e) {
            // Departments are optional - ignore fetch failures silently.
        }
    }, []);

    useEffect(() => { fetchRows(); fetchDepartments(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const openAdd = () => {
        setPopouts(prev => [...prev, { key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, mode: 'add', row: null }]);
    };

    const openForm = (row, mode) => {
        const key = `row-${row.id}-${mode}`;
        // Reuse the window if this record is already open in that mode.
        setPopouts(prev => prev.some(p => p.key === key) ? prev : [...prev, { key, mode, row }]);
    };

    const closePopout = (key, refresh) => {
        setPopouts(prev => prev.filter(p => p.key !== key));
        if (refresh) fetchRows();
    };

    const handleDelete = async () => {
        const row = deleteConfirm;
        if (!row) return;
        setDeleting(true);
        try {
            const res = await fetch(`${API_BASE}${row.id}/`, { method: 'DELETE', headers: getAuthHeaders(false) });
            if (!res.ok && res.status !== 204) throw new Error('Failed to delete');
            toast.success('Quick Reference deleted.');
            setDeleteConfirm(null);
            fetchRows();
        } catch (e) {
            toast.error(e.message || 'Failed to delete');
        } finally {
            setDeleting(false);
        }
    };

    const openJobPreview = async (row) => {
        if (!row.job) return;
        setJobLoading(true);
        try {
            const res = await fetch(`${JOBS_URL}${row.job}/`, { headers: getAuthHeaders(false) });
            if (!res.ok) throw new Error('Failed to load job');
            const job = await res.json();
            setJobPreview({ open: true, job });
        } catch (e) {
            toast.error(e.message || 'Unable to open job');
        } finally {
            setJobLoading(false);
        }
    };

    if (!canView) {
        return <div style={{ padding: 20 }}><h2>Permission Denied</h2><p>You do not have permission to view Quick Reference.</p></div>;
    }

    return (
        <div style={{ padding: 20 }}>
            <div className="list-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0 }}>Quick Reference</h2>
                {canCreate && (
                    <button className="add-btn" onClick={openAdd}>Add New Quick Reference</button>
                )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
                <input
                    className="search-input"
                    placeholder="Search subject or IDF/BL..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') fetchRows(search); }}
                    style={{ width: 280 }}
                />
                <button className="add-btn" style={{ background: '#007bff' }} onClick={() => fetchRows(search)}>Search</button>
                <button className="add-btn" style={{ background: '#6c757d' }} onClick={() => { setSearch(''); fetchRows(''); }}>Clear</button>
            </div>

            {loading && <div className="loading-indicator">Loading...</div>}

            {!loading && (
                <div style={{ overflowX: 'auto' }}>
                    <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
                        <thead>
                            <tr>
                                {['SN', 'SUBJECT', 'DETAILS', 'IDF / BL / JOB', 'CREATED BY', 'UPDATED BY', 'APPROVED BY', 'REJECTED REASON', 'ACTION'].map(h => (
                                    <th key={h} style={{ whiteSpace: 'nowrap', fontSize: 12, border: '1px solid #ddd', padding: '6px', textAlign: h === 'ACTION' ? 'center' : 'left' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, idx) => (
                                <tr key={row.id}>
                                    <td style={{ border: '1px solid #ddd', padding: '6px', fontSize: 12 }}>{idx + 1}</td>
                                    <td style={{ border: '1px solid #ddd', padding: '6px', fontSize: 12 }}>
                                        <span>{(row.subject || '').toUpperCase()}</span>
                                        {row.status !== 'APPROVED' && (
                                            <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 'bold', color: row.status === 'REJECTED' ? '#c62828' : '#b8860b' }}>
                                                [{row.status}]
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '6px', fontSize: 12 }} title={detailsPreview(row.details)}>
                                        {detailsPreview(row.details) || '-'}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '6px', fontSize: 12 }}>
                                        {row.job ? (
                                            <a
                                                href="#"
                                                onClick={e => { e.preventDefault(); openJobPreview(row); }}
                                                style={{ color: '#007bff', textDecoration: 'underline', cursor: 'pointer' }}
                                                title="Open job (view only)"
                                            >
                                                {jobLoading ? 'Loading…' : (row.job_label || `JOB-${row.job}`)}
                                            </a>
                                        ) : '-'}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '6px', fontSize: 12, whiteSpace: 'nowrap' }}>
                                        <div>{row.created_by_name || '-'}</div>
                                        <div style={{ fontSize: 10, color: '#888' }}>{fmtDateTime(row.created_at)}</div>
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '6px', fontSize: 12, whiteSpace: 'nowrap' }}>
                                        <div>{row.updated_by_name || '-'}</div>
                                        <div style={{ fontSize: 10, color: '#888' }}>{fmtDateTime(row.updated_at)}</div>
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '6px', fontSize: 12, whiteSpace: 'nowrap' }}>
                                        {row.status === 'APPROVED' ? (
                                            <>
                                                <div>{row.decision_by_name || '-'}</div>
                                                <div style={{ fontSize: 10, color: '#888' }}>{fmtDateTime(row.decision_at)}</div>
                                            </>
                                        ) : '-'}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '6px', fontSize: 12, color: '#c62828' }}>
                                        {row.status === 'REJECTED' ? (row.reject_reason || '-') : ''}
                                    </td>
                                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                        <button className="icon-btn" title="View" onClick={() => openForm(row, 'view')} style={{ color: '#007bff' }}>
                                            <i className="fa fa-eye" />
                                        </button>
                                        {(canDelete || (canEdit && row.status === 'REJECTED')) && (
                                            <button className="icon-btn" title="Edit" onClick={() => openForm(row, 'edit')} style={{ color: '#2e7d32' }}>
                                                <i className="fa fa-pencil" />
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button className="icon-btn cancel-btn" title="Delete" onClick={() => setDeleteConfirm(row)}>
                                                <i className="fa fa-trash" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan={9} className="no-data" style={{ textAlign: 'center', padding: 20 }}>No records found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {popouts.map(p => (
                <PopoutWindow
                    key={p.key}
                    title={`${p.mode === 'view' ? 'View' : p.mode === 'edit' ? 'Edit' : 'New'} Quick Reference`}
                    name={`quick_reference_form_${p.key}`}
                    toastContainerId={`qr-popout-toast-${p.key}`}
                    onClose={() => closePopout(p.key, false)}
                >
                    <QuickReferenceForm
                        mode={p.mode}
                        initialData={p.row}
                        departments={departments}
                        canDelete={canDelete}
                        onClose={() => closePopout(p.key, false)}
                        onSave={() => closePopout(p.key, true)}
                        toastContainerId={`qr-popout-toast-${p.key}`}
                    />
                </PopoutWindow>
            ))}

            {deleteConfirm && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{ width: 480, maxWidth: '95%' }}>
                        <div className="dialog-header" style={{ background: '#dc3545', color: '#fff' }}>
                            <h3 style={{ margin: 0, color: '#fff' }}>Delete Quick Reference</h3>
                        </div>
                        <div className="dialog-body" style={{ padding: 20 }}>
                            <p style={{ margin: 0 }}>Are you sure you want to delete <strong>{(deleteConfirm.subject || '').toUpperCase()}</strong>? This action cannot be undone.</p>
                        </div>
                        <div className="dialog-footer">
                            <button className="dialog-btn" style={{ background: '#dc3545', color: '#fff' }} disabled={deleting} onClick={handleDelete}>
                                {deleting ? 'Deleting…' : 'Yes, Delete'}
                            </button>
                            <button className="dialog-btn dialog-btn-secondary" onClick={() => setDeleteConfirm(null)}>No</button>
                        </div>
                    </div>
                </div>
            )}

            {jobPreview.open && jobPreview.job && (
                <PopoutWindow
                    title={`Job ${jobPreview.job.id} — View Only`}
                    name={`qr_job_view_${jobPreview.job.id}`}
                    onClose={() => setJobPreview({ open: false, job: null })}
                >
                    <JobForm
                        mode="view"
                        initialData={jobPreview.job}
                        onClose={() => setJobPreview({ open: false, job: null })}
                        documentsEditable={false}
                    />
                </PopoutWindow>
            )}
        </div>
    );
};

export default QuickReference;
